import {
    Box,
    Chip,
    Divider,
    Stack,
    Typography,
    useTheme,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    Menu,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CardContent,
    Card,
    FormControl,
    Select,
    InputLabel,
    MenuItem,
    Popover,
    Button,
    TextField, 
    DialogActions, 
    CircularProgress, 
    ToggleButtonGroup, 
    ToggleButton,
    Grid,
    Alert,
    Tooltip,
    FormControlLabel,
    Switch,
} from "@mui/material";
import PaginatedServerSideDataTable from "@components/PaginatedServerSideDataTable";
import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ApiRequestFailed, authenticateAndFetchData, retryFn, authenticateAndPostData } from "@lib/apis";
import { ColDef, ValueFormatterParams } from "ag-grid-community";
import PageLoading from "@components/PageLoading";
import PageDataErrorHandler from "@components/PageDataErrorHandler";
import { CustomCellRendererProps } from "ag-grid-react";
import { useNavigate, Link } from "react-router-dom";
import { urls } from "@routes";
import { format } from "date-fns";
import {useDialogs} from "@toolpad/core";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useSnackbar } from "notistack";
import { Close, Domain, Visibility, Email, ContentCopy, Downloading, ArrowBack, Delete, Edit, Info, MoreVert, PlayArrow, Replay, LocalFireDepartment } from "@mui/icons-material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { getExportFilename } from "@lib/utils";
import {useAuth} from "./AuthProvider";

const TEST_EMAIL_EDITOR_MODULES = {
    toolbar: [
        [{ font: [] }, { size: [] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ indent: "-1" }, { indent: "+1" }],
        [{ align: [] }],
        ["blockquote", "link"],
        ["clean"],
    ],
};

interface EmailAccount {
    id: number;
    email_address: string;
    username: string;
    email_active: boolean;
    active_contacts_count: number;
}

interface Subdomain {
    id: number;
    subdomain: string;
    created_on_ts: number;
    email_accounts: EmailAccount[];
}

interface MDomain {
    id: number;
    domain: string;
    user_email: string;
    user_id: number;
    workspace_name: string;
    workspace_id: number;
    created_on_ts: number;
    setup_complete: boolean;
    current_setup_stage: number;
    naming_strategy: string;
    custom_name: string | null;
    contacts_count: number;
    aws_hosted_zone_id: string | null;
    status: string;
    warmup_progress_percent: number;
    warmup_days_remaining: number;
    warmup_active_days: number;
    redirect_subdomains_to: string | null;
    blacklist_status: string;
    active_campaigns: string[];
    subdomains: Subdomain[];
    ns_status: string    
    ns_mismatch_detected_at: string
    last_ns_check_at: string
    burnt: boolean;
    burnt_detected_on: number | null;
    burnt_via: string;
    burnt_at: string;
    registrar_name: string
	registrar_icon_name: string
	is_automated_import: boolean
}

interface NSRecordResponse {
    current_ns_records: string[];
    required_ns_records: string[];
}

interface PaginationData {
    data: MDomain[];
    total_count: number;
    total_pages: number;
    current_page: number;
    page_size: number;
    summary: summary;
}

interface summary {
    total_domains: number;
    setup_complete_count: number;
    setup_incomplete_count: number;
    naming_custom_count: number;
    naming_random_count: number;
    naming_female_count: number;
    naming_male_count: number;
}

interface PageData {
    status_code: number;
    status_text: string;
    data: MDomain[];
    total_count: number;
    total_pages: number;
    current_page: number;
    page_size: number;
    summary: summary;
}

export default function AdminAllDomains() {
    const theme = useTheme();
    const { user } = useAuth();
    const navigate = useNavigate();
    const dialogs = useDialogs();
    const { enqueueSnackbar } = useSnackbar();
    
    const [pageData, setPageData] = useState<PaginationData>();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedDomain, setSelectedDomain] = useState<MDomain | null>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [filterAnchor, setFilterAnchor] = useState(null);

    const openFilter = (e) => setFilterAnchor(e.currentTarget);
    const closeFilter = () => setFilterAnchor(null);

    const isFilterOpen = Boolean(filterAnchor);

    const [filterSetupComplete, setFilterSetupComplete] = useState("");
    const [filterWarmup, setFilterWarmup] = useState("");
    const [filterBlacklist, setFilterBlacklist] = useState("");

    const [sendEmailDialogOpen, setSendEmailDialogOpen] = useState(false);    

    const [nsModalOpen, setNsModalOpen] = useState(false);  
    const [selectedNsDomain, setSelectedNsDomain] = useState<MDomain | null>(null);
    const [nsRecords, setNsRecords] = useState<NSRecordResponse | null>(null);

    // Query to fetch all domains with pagination
    const fetchAllDomainsQuery = useQuery({
        queryKey: ["adminAllDomainsQuery", currentPage, pageSize, searchQuery],
        queryFn: () => {
            let url = `/admin/all-connected-domains/?page=${currentPage}&page_size=${pageSize}`;
            if (searchQuery.trim()) {
                url += `&search=${encodeURIComponent(searchQuery)}`;
            }
            if (filterSetupComplete) {
                url += `&setup_complete=${filterSetupComplete}`;
            }
            if (filterWarmup) {
                url += `&warmup=${filterWarmup}`;
            }
            if (filterBlacklist) {
                url += `&blacklist=${filterBlacklist}`;
            }

            return authenticateAndFetchData(url);
        },
        gcTime: 0,
        refetchOnWindowFocus: false,
        retry: retryFn,
    });

    const fetchAllDomainsExportQuery = useQuery({
        queryKey: ["adminAllDomainsExportQuery"],
        queryFn: () => {
            let url = `/admin/all-domain/export/`;
            return authenticateAndFetchData(url);
        },
        gcTime: 0,
        refetchOnWindowFocus: false,
        retry: retryFn,
    });

    useEffect(() => {
        if (fetchAllDomainsQuery.data) {
            const data = fetchAllDomainsQuery.data.data as PageData;
            setPageData({
                data: data.data,
                total_count: data.total_count,
                total_pages: data.total_pages,
                current_page: data.current_page,
                page_size: data.page_size,
                summary: data.summary,
            });
        }
    }, [fetchAllDomainsQuery.data]);

    const editDomainWarmupLimitMutation = useMutation({
        mutationKey: ["adminEmailAccountEditDomainWarmupLimit"],
        mutationFn: (data: {
            emailAccountId: number,
            newLimit: number
        }) => authenticateAndPostData("/admin/warmup/edit-domain-limit/", {
            id: data.emailAccountId,
            new_limit: data.newLimit,
        }),
        gcTime: 0,
        retry: retryFn,
        onSuccess: () => {
            if (pageData) {
                // const domains: string[] = pageData.connected_domains.map(value => value["domain"])
                // fetchWarmupDataMutation.mutate(domains);
                fetchAllDomainsQuery.refetch().then();
                enqueueSnackbar("Domain warmup limit updated successfully!", {
                    variant: "success",
                });
            } else {
                console.error("Page data not set.")
            }
        },
        onError: (error: ApiRequestFailed) => {
            console.error(error);
            enqueueSnackbar(error.data.message, {
                variant: "error",
            });
        }
    });
    
    const deleteDomainMutation = useMutation({
        mutationKey: ["adminDeleteDomainMutation"],
        mutationFn: (domain_id: string) => authenticateAndPostData("/admin/delete-domain/", {
            id: domain_id
        }),
        gcTime: 0,
        retry: retryFn,
        onSuccess: () => {
            enqueueSnackbar("Domain is being deleted. It might take a few seconds. Please refresh and check later.", {
                variant: "success",
            });
            // adminUserConnectedDomainsQuery.refetch().then();
        },
        onError: (error: ApiRequestFailed) => {
            console.error(error);
            enqueueSnackbar(error.data.message, {
                variant: "error",
            });
        }
    })

    const fetchNSRecordMutation = useMutation({
        mutationKey: ["adminFetchNSRecordData"],
        mutationFn: (data: { workspace_id: number; domain: string }) => 			
            authenticateAndPostData(
                `/admin/admin-domain-ns-record/?workspace_id=${data.workspace_id}&domain=${data.domain}`, 
                {}
            ),
        gcTime: 0,
        retry: retryFn,
        onSuccess: (response) => {
            const data: NSRecordResponse = response.data;
            setNsRecords(data);
        },
        onError: (error: ApiRequestFailed) => {
            console.error(error);
            enqueueSnackbar(error.data?.message || "Failed to fetch NS records", {
                variant: "error",
            });
            setNsModalOpen(false);
        }
    });    

    const retryPostalSetupMutation = useMutation({
        mutationKey: ["retryPostalSetup"],
        mutationFn: (msub_id: number) =>
            authenticateAndPostData("/admin/retry-postal-setup/", {
                msub_id: msub_id,
            }),
        gcTime: 0,
        retry: retryFn,
        onSuccess: () => {
            fetchAllDomainsQuery.refetch().then();
            enqueueSnackbar("Retry in progress. Your domain setup is in progress again. It should take around 3-5 mins.", {
                variant: "success",
            });			
        },
        onError: (error: ApiRequestFailed) => {
            console.error(error);
            enqueueSnackbar(error.data?.message || "Failed to Retry Postal setup", {
                variant: "error",
            });			
        },
    });

    const unmarkBurntMutation = useMutation({
        mutationKey: ["unmarkDomainBurnt"],
        mutationFn: (domainId: number) =>
            authenticateAndPostData("/admin/domain/unmark-burnt/", {
                id: domainId,
            }),
        gcTime: 0,
        retry: retryFn,
        onSuccess: () => {
            fetchAllDomainsQuery.refetch().then();
            enqueueSnackbar("Domain unmarked as burnt. Warmup will resume, campaigns stay paused.", {
                variant: "success",
            });
        },
        onError: (error: ApiRequestFailed) => {
            console.error(error);
            enqueueSnackbar(error.data?.message || "Failed to unmark domain as burnt", {
                variant: "error",
            });
        },
    });
        
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        setCurrentPage(1); // Reset to first page when changing page size
    };

    const handleSearchChange = (search: string) => {
        setSearchQuery(search);
        setCurrentPage(1); // Reset to first page when searching
    };

    const handleOpenDialog = (domain: MDomain) => {
        setSelectedDomain(domain);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedDomain(null);
    };

    const downloadDomainCSV = (domains: MDomain[]) => {
        if (!domains || domains.length === 0) {
            enqueueSnackbar("No data to export.", { variant: "error" });
            return;
        }

        const escapeCSV = (value: any) => {
            if (value == null) return "";
            const stringValue = String(value);
            return `"${stringValue.replace(/"/g, '""')}"`;
        };

        // -------------------------------
        // 1. CSV HEADERS
        // -------------------------------
        const csvHeader = [
            "Domain ID",
            "Domain",
            "User Email",
            "User ID",
            "Workspace Name",
            "Workspace ID",
            "Created On",
            "Setup Complete",
            "Setup Stage",
            "Naming Strategy",
            "Custom Name",
            "Contacts Count",
            "Hosted Zone ID",
            "Status",
            "Warmup Progress %",
            "Warmup Days Remaining",
            "Warmup Active Days",
            "Redirect Subdomains To",
            "Blacklist Status",
            "Burnt",
            "Burnt On",
            "Burnt Via",
            "Burnt At",
            "Active Campaigns",
            "Subdomains Count",
            "Email Accounts Count",
            "Subdomains",
            "Email Accounts",
            "Domain Registrar Name",
		    "Registrar Icon",
		    "Import Type",
        ];

        // -------------------------------
        // 2. CSV ROWS
        // -------------------------------
        const csvRows = domains.map(domain => {
            const subdomainsCount = domain.subdomains.length;

            const emailAccountsCount = domain.subdomains.reduce(
                (acc, sub) => acc + sub.email_accounts.length,
                0
            );
            
            // Flatten subdomains list
            const subdomainsString = domain.subdomains
                .map(sub => `${sub.subdomain} (ID: ${sub.id})`)
                .join(" | ");

            // Flatten full email account info
            const emailAccountsFullString = domain.subdomains
                .flatMap(sub =>
                    sub.email_accounts.map(acc =>
                        `email=${acc.email_address} | user=${acc.username} | id=${acc.id} | active=${acc.email_active} | contacts=${acc.active_contacts_count}`
                    )
                )
                .join(" || ");

            return [
                escapeCSV(domain.id),
                escapeCSV(domain.domain),
                escapeCSV(domain.user_email),
                escapeCSV(domain.user_id),
                escapeCSV(domain.workspace_name),
                escapeCSV(domain.workspace_id),
                escapeCSV(format(new Date(domain.created_on_ts), "yyyy-MM-dd HH:mm:ss")),                
                escapeCSV(domain.setup_complete),
                escapeCSV(domain.current_setup_stage),
                escapeCSV(domain.naming_strategy),
                escapeCSV(domain.custom_name),
                escapeCSV(domain.contacts_count),
                escapeCSV(domain.aws_hosted_zone_id),
                escapeCSV(domain.status),
                escapeCSV(domain.warmup_progress_percent),
                escapeCSV(domain.warmup_days_remaining),
                escapeCSV(domain.warmup_active_days),
                escapeCSV(domain.redirect_subdomains_to),
                escapeCSV(domain.blacklist_status),
                escapeCSV(domain.burnt ? "Yes" : "No"),
                escapeCSV(domain.burnt_detected_on
                    ? format(new Date(domain.burnt_detected_on), "yyyy-MM-dd HH:mm:ss")
                    : ""),
                escapeCSV(domain.burnt_via),
                escapeCSV(domain.burnt_at),
                escapeCSV(domain.active_campaigns.join(",")),
                escapeCSV(subdomainsCount),
                escapeCSV(emailAccountsCount),
                escapeCSV(subdomainsString),
                escapeCSV(emailAccountsFullString),
                escapeCSV(domain.registrar_name || "Manual"),
                escapeCSV(domain.registrar_icon_name || ""),
                escapeCSV(domain.is_automated_import === true ? "Automated" : "Manual"),
            ];
        });

        const csvContent = [csvHeader, ...csvRows].map(r => r.join(",")).join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = getExportFilename(user?.email ?? "unknown", "AllDomains");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleExportClick = () => {
        try {
            const domains = fetchAllDomainsExportQuery?.data?.data.data;

            if (!domains || domains.length === 0) {
                enqueueSnackbar("No data available for export.", { variant: "error" });
                return;
            }

            downloadDomainCSV(domains);
            enqueueSnackbar("CSV downloaded successfully!", { variant: "success" });
        } catch (err) {
            console.error(err);
            enqueueSnackbar("Error while exporting domain CSV.", { variant: "error" });
        }
    };

    const handleOpenNameserverDialog = (rowData: MDomain) => {
		setSelectedDomain(rowData);
		setNsRecords(null); // Reset previous state data
		setNsModalOpen(true);
		
		// Fire the mutation request with the row parameters
		fetchNSRecordMutation.mutate({
			workspace_id: rowData.workspace_id,
			domain: rowData.domain
		});
	};

	const copyToClipboard = (text: string, successMessage: string) => {
		navigator.clipboard.writeText(text).then(() => {
			enqueueSnackbar(successMessage, { variant: "success", autoHideDuration: 2000 });
		}).catch(() => {
			enqueueSnackbar("Failed to copy to clipboard", { variant: "error" });
		});
	};

    const ExportDomainCsvButton = () => {
            return (
                <Button startIcon={<Downloading />}
                    variant="contained"
                    color="primary"
                    disabled={fetchAllDomainsExportQuery.isPending}
                    onClick={() => {
                        handleExportClick();
                    }}>
                    Export
                </Button>
            )
        }


    const UserEmailLink = (cellProps: CustomCellRendererProps) => {
        return (
            <Link
                to={urls["adminUserDetails"].replace(":userId", cellProps.data["user_id"])}
                style={{ color: theme.palette.text.primary }}
            >
                {cellProps.value}
            </Link>
        );
    };

    const SubdomainsRenderer = (cellProps: CustomCellRendererProps) => {
        const subdomainsCount = cellProps.data.subdomains?.length || 0;
        const emailAccountsCount = cellProps.data.subdomains?.reduce(
            (acc: number, sub: Subdomain) => acc + (sub.email_accounts?.length || 0),
            0
        ) || 0;

        return (
            <Box display="flex" alignItems="center" gap={1}>
                <Chip label={`${subdomainsCount} subdomain(s)`} size="small" variant="outlined" />
                <Chip label={`${emailAccountsCount} email(s)`} size="small" variant="outlined" />
                <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(cellProps.data)}
                    title="View details"
                >
                    <Visibility fontSize="small" />
                </IconButton>
            </Box>
        );
    };

    const DomainCellRenderer = (cellProps: CustomCellRendererProps) => {
        return (
            <Box display="flex" alignItems="center">
                {cellProps.value}
            </Box>
        );
    };

    const WorkspaceNameLink = (cellProps: CustomCellRendererProps) => {
        return (
            <Link
                to={urls["adminUserWorkspaces"].replace(":userId", cellProps.data["user_id"])}
                style={{ color: theme.palette.text.primary }}
            >
                {cellProps.value}
            </Link>
        );
    };

    function timestampFormatter(params: ValueFormatterParams) {
        if (params.value === null) {
            return "---";
        }
        return format(new Date(params.value), "do MMM y, h:mm a");
    }

    const SetupCompleteRenderer = (cellProps: CustomCellRendererProps) => {
        return cellProps.value ? (
            <Chip label="Yes" color="success" size="small" />
        ) : (
            <Chip label="No" color="error" size="small" />
        );
    };

    const BlacklistStatusRenderer = (cellProps: CustomCellRendererProps) => {
        const status = cellProps.value || "Unknown";
        let color: "success" | "error" | "warning" | "default" = "default";

        if (status === "Healthy") {
            color = "success";
        } else if (status === "Incomplete" || status === "Unknown Error") {
            color = "warning";
        } else if (status !== "Healthy" && status !== "Incomplete") {
            color = "error";
        }

        return <Chip label={status} color={color} size="small" />;
    };

    const ActiveCampaignsRenderer = (cellProps: CustomCellRendererProps) => {
        const campaigns = cellProps.value as string[];
        if (!campaigns || campaigns.length === 0) {
            return <Typography variant="body2">---</Typography>;
        }
        return (
            <Box display="flex" gap={0.5} flexWrap="wrap">
                {campaigns.slice(0, 2).map((campaign, idx) => (
                    <Chip key={idx} label={campaign} size="small" variant="outlined" />
                ))}
                {campaigns.length > 2 && (
                    <Chip label={`+${campaigns.length - 2} more`} size="small" variant="outlined" />
                )}
            </Box>
        );
    };

    const WarmupProgressRenderer = (cellProps: CustomCellRendererProps) => {
        const progress = cellProps.value || 0;
        return <Typography variant="body2">{progress}%</Typography>;
    };

    const NsStatusRenderer = (cellProps: CustomCellRendererProps) => {
        const status = cellProps.value as string | null;

        if (!status) {
            return <span style={{ color: "#999" }}>---</span>;
        }

        let label = "";
        let color: "success" | "error" | "warning" | "default" = "default";

        if (status === "healthy") {
            label = "Healthy";
            color = "success";
        } else if (status === "mismatch") {
            label = "Mismatch";
            color = "error";
        }

        return <Chip label={label} color={color} size="small" />;
    };


    const BurntStatusRenderer = (cellProps: CustomCellRendererProps) => {
        return cellProps.value ? (
            <Chip label="Burnt" color="error" size="small" />
        ) : (
            <span style={{ color: "#999" }}>---</span>
        );
    };


    // Define dialog content for viewing subdomains and email accounts
    const SubdomainsDialog = () => {
        if (!selectedDomain) return null;

        return (
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">
                            Subdomains & Email Accounts for {selectedDomain.domain}
                        </Typography>
                        <IconButton onClick={handleCloseDialog} size="small">
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedDomain.subdomains && selectedDomain.subdomains.length > 0 ? (
                        selectedDomain.subdomains.map((subdomain) => (
                            <Box key={subdomain.id} sx={{ mb: 3 }}>
                                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: "bold" }}>
                                    Subdomain: {subdomain.subdomain}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                                    Created: {format(new Date(subdomain.created_on_ts), "do MMM y, h:mm a")}
                                </Typography>
                                {subdomain.email_accounts && subdomain.email_accounts.length > 0 ? (
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell><strong>Email Address</strong></TableCell>
                                                    <TableCell><strong>Username</strong></TableCell>
                                                    <TableCell><strong>Status</strong></TableCell>
                                                    <TableCell><strong>Active Contacts</strong></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {subdomain.email_accounts.map((email) => (
                                                    <TableRow key={email.id}>
                                                        <TableCell>{email.email_address}</TableCell>
                                                        <TableCell>{email.username}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={email.email_active ? "Active" : "Inactive"}
                                                                color={email.email_active ? "success" : "default"}
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                        <TableCell>{email.active_contacts_count}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                ) : (
                                    <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary" }}>
                                        No email accounts
                                    </Typography>
                                )}
                                <Divider sx={{ mt: 2 }} />
                            </Box>
                        ))
                    ) : (
                        <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary" }}>
                            No subdomains available
                        </Typography>
                    )}
                </DialogContent>
            </Dialog>
        );
    };

    const NameserverDialog = () => {
        // If you renamed the variable to selectedNsDomain in the previous step, update this reference accordingly
        if (!selectedDomain) return null;

        const isLoading = fetchNSRecordMutation.isPending;
        const requiredRecords = nsRecords?.required_ns_records || [];

        return (
            <Dialog
                open={nsModalOpen}
                onClose={() => setNsModalOpen(false)}
                maxWidth="sm" // Changed from 'md' to 'sm' for a better single-column fit
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Required Nameservers for {selectedDomain.domain}
                        </Typography>
                        <IconButton onClick={() => setNsModalOpen(false)} size="small">
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent dividers sx={{ minHeight: 180, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                    {isLoading ? (
                        <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={4}>
                            <CircularProgress size={40} />
                            <Typography variant="body2" color="text.secondary">
                                Fetching configuration records...
                            </Typography>
                        </Box>
                    ) : nsRecords ? (
                        <Box sx={{ width: "100%", py: 1 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5 }}>
                                Required AWS Records ({requiredRecords.length})
                            </Typography>
                            
                            <Stack spacing={1.5}>
                                {requiredRecords.length > 0 ? (
                                    requiredRecords.map((ns, idx) => (
                                        <Card key={idx} variant="outlined" sx={{ 
                                                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.default' : '#fafafa',
                                                borderRadius: 2 
                                            }}>
                                            <CardContent sx={{ 
                                                display: "flex", 
                                                justifyContent: "space-between", 
                                                alignItems: "center", 
                                                p: "12px !important" 
                                            }}>
                                                <Typography variant="body2" component="code" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                                                    {ns}
                                                </Typography>
                                                <Tooltip title="Copy Record">
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => copyToClipboard(ns, `Copied: ${ns}`)}
                                                        color="primary"
                                                    >
                                                        <ContentCopy fontSize="inherit" />
                                                    </IconButton>
                                                </Tooltip>
                                            </CardContent>
                                        </Card>
                                    ))
                                ) : (
                                    <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary", py: 1 }}>
                                        No records found.
                                    </Typography>
                                )}
                            </Stack>
                        </Box>
                    ) : (
                        <Alert severity="error" sx={{ width: "100%" }}>
                            Failed to fetch nameserver details. Please close and try again.
                        </Alert>
                    )}
                </DialogContent>
                
                <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
                    {nsRecords && requiredRecords.length > 0 ? (
                        <Button
                            variant="outlined"
                            startIcon={<ContentCopy />}
                            onClick={() => copyToClipboard(requiredRecords.join("\n"), "Copied all required entries!")}
                            sx={{ borderRadius: 2 }}
                        >
                            Copy All Records
                        </Button>
                    ) : <Box />}
                    
                    <Button variant="contained" onClick={() => setNsModalOpen(false)} sx={{ borderRadius: 2, px: 3 }}>
                        Close View
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    const ActionsButton = (props: CustomCellRendererProps) => {
        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
        const open = Boolean(anchorEl);

        const handleClick = (event: any) => {
            setAnchorEl(event.currentTarget);
        };

        const handleClose = () => {
            setAnchorEl(null);
        };

        const handleResumeSetup = () => {
            const subdomain = props.data["domain"];
            navigate(
                `${urls["adminConnectDomain"]
                .replace(':userId', props.data["user_id"] ?? "")
                .replace(':workspaceId', props.data["workspace_id"] ?? "")}?subdomain=${subdomain}`
            );
            handleClose();
        };

        const handleRetrySetup = () => {
			retryPostalSetupMutation.mutate(props.data["id"]);
			handleClose();
		};
        
        const handleDelete = async () => {
            if (props.data["active_campaigns"].length > 0) {
                const deleteConfirmed = await dialogs.confirm(
                    (
                        <DialogContentText sx={{whiteSpace: "pre-line"}}>
                            This will permanently remove <b>{props.data["domain"]}</b> from DeliverymanAI.
                            This domain is currently being used in the following active campaigns:
                            <br/><br/>
                            {props.data["active_campaigns"].join("\n")}
                            <br/><br/>
                            Proceed?
                        </DialogContentText>
                    ),
                    {
                        title: `Confirm deleting ${props.data["domain"]}?`,
                        okText: "Proceed",
                    }
                );

                if (deleteConfirmed) {
                    deleteDomainMutation.mutate(props.data["id"]);
                }
            } else {
                const deleteConfirmed = await dialogs.confirm(
                    (
                        <DialogContentText sx={{whiteSpace: "pre-line"}}>
                            This will permanently remove <b>{props.data["domain"]}</b> from DeliverymanAI. Proceed?
                        </DialogContentText>
                    ),
                    {
                        title: `Confirm deleting ${props.data["domain"]}?`,
                        okText: "Proceed",
                    }
                );

                if (deleteConfirmed) {
                    deleteDomainMutation.mutate(props.data["id"]);
                }
            }

            handleClose();
        };

        return (
            <>
                <IconButton
                    aria-label="more"
                    aria-controls={open ? "long-menu" : undefined}
                    aria-expanded={open ? "true" : undefined}
                    aria-haspopup="true"
                    onClick={handleClick}
                >
                    <MoreVert/>
                </IconButton>
                <Menu
                    id="long-menu"
                    MenuListProps={{
                        "aria-labelledby": "long-button",
                    }}
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                >
                    {/*<MenuItem*/}
                    {/*	onClick={() => {*/}
                    {/*		// setEditWarmupDateTime(props.data);*/}
                    {/*		setOpenEditWarmup(true);*/}
                    {/*	}}*/}
                    {/*>*/}
                    {/*	<Edit color={"warning"} sx={{mr: 1}}/>*/}
                    {/*	<Typography variant="subtitle2">Edit Warmup</Typography>*/}
                    {/*</MenuItem>*/}

                    <MenuItem onClick={async () => {
                        const limitInput: string | null = await dialogs.prompt(
                            "Enter new limit (needs to be multiple of current daily increment value)"
                        )
                        if (limitInput !== null) {
                            const limitInputAsNumber = parseInt(limitInput);
                            if (isNaN(limitInputAsNumber)) {
                                enqueueSnackbar("Please enter a valid number", {
                                    variant: "error",
                                });
                                return
                            }

                            // Make request.
                            editDomainWarmupLimitMutation.mutate({
                                emailAccountId: props.data["id"],
                                newLimit: limitInputAsNumber,
                            });
                        }
                    }}>
                        <Edit color={"warning"} sx={{mr: 1}}/>
                        <Typography variant="subtitle2">Edit Warmup</Typography>
                    </MenuItem>
                    {!props.data["setup_complete"] && (
                        <MenuItem onClick={handleResumeSetup}>
                            <PlayArrow color="success" sx={{mr: 1}}/>
                            <Typography variant="subtitle2">Resume Setup</Typography>
                        </MenuItem>
                    )}
                    {props.data["status"] === "postal_error" && (
                        <MenuItem 
                            onClick={handleRetrySetup}
                            disabled={retryPostalSetupMutation.isPending}
                        >
                            <Replay color={retryPostalSetupMutation.isPending ? "disabled" : "primary"} sx={{ mr: 1 }} />
                            <Typography variant="subtitle2">
                                {retryPostalSetupMutation.isPending ? "Retrying..." : "Retry Setup"}
                            </Typography>
                        </MenuItem>
                    )}		
                    {props.data["burnt"] && (
                        <MenuItem
                            onClick={() => {
                                handleClose();
                                unmarkBurntMutation.mutate(props.data["id"]);
                            }}
                            disabled={unmarkBurntMutation.isPending}
                        >
                            <LocalFireDepartment color={unmarkBurntMutation.isPending ? "disabled" : "warning"} sx={{mr: 1}}/>
                            <Typography variant="subtitle2">
                                {unmarkBurntMutation.isPending ? "Unmarking..." : "Unmark Burnt"}
                            </Typography>
                        </MenuItem>
                    )}
                    <MenuItem onClick={handleDelete}>
                        <Delete color="error" sx={{mr: 1}}/>
                        <Typography variant="subtitle2">Delete Domain</Typography>
                    </MenuItem>
                </Menu>
            </>
        );
    };
    
    const columnCustomizerLabel = {
		domain: "Domain",
		user_email: "User Email",
		workspace_name: "Workspace",
		subdomains: "Subdomains & Emails",
		created_on_ts: "Created On",
		setup_complete: "Setup Complete",
		current_setup_stage: "Setup Stage",
		naming_strategy: "Naming Strategy",
        contacts_count: "Contacts",
		warmup_progress_percent: "Warmup Progress",
		warmup_days_remaining: "Warmup Days Left",
        blacklist_status: "Blacklist Status",
		active_campaigns: "Active Campaigns",
		ns_status: "NS Status",
        ns_mismatch_detected_at: "NS Issue Since",
		last_ns_check_at: "Last NS Check",		
		status: "Status",						
		burnt: "Burnt",
		burnt_detected_on: "Burnt On",
		burnt_via: "Burnt Via",
		burnt_at: "Burnt At",
        registrar_name: "Domain Registrar Name",
		registrar_icon_name: "Registrar Icon",
		is_automated_import: "Import Type",
	};

    // Columns for domains table
    const columnDefs: ColDef[] = [
        {
            field: "domain",
            headerName: "Domain",
            cellRenderer: DomainCellRenderer,
            minWidth: 200,
        },
        {
            field: "user_email",
            headerName: "User Email",
            cellRenderer: UserEmailLink,
            minWidth: 200,
        },
        {
            field: "workspace_name",
            headerName: "Workspace",
            cellRenderer: WorkspaceNameLink,
            minWidth: 150,
        },
        {
            field: "subdomains",
            headerName: "Subdomains & Emails",
            cellRenderer: SubdomainsRenderer,
            minWidth: 250,
            sortable: false,
        },
        {
            field: "created_on_ts",
            headerName: "Created On",
            valueFormatter: timestampFormatter,
            minWidth: 180,
        },
        {
            field: "setup_complete",
            headerName: "Setup Complete",
            cellRenderer: SetupCompleteRenderer,
            minWidth: 140,
        },
        {
            field: "current_setup_stage",
            headerName: "Setup Stage",
            minWidth: 120,
        },
        {
            field: "naming_strategy",
            headerName: "Naming Strategy",
            minWidth: 150,
        },
        {
            field: "contacts_count",
            headerName: "Contacts",
            minWidth: 100,
        },
        {
            field: "status",
            headerName: "Status",
            minWidth: 120,
        },
        {
            field: "warmup_progress_percent",
            headerName: "Warmup Progress",
            cellRenderer: WarmupProgressRenderer,
            minWidth: 150,
        },
        {
            field: "warmup_days_remaining",
            headerName: "Warmup Days Left",
            minWidth: 160,
        },
        {
            field: "blacklist_status",
            headerName: "Blacklist Status",
            cellRenderer: BlacklistStatusRenderer,
            minWidth: 150,
        },
        {
            field: "active_campaigns",
            headerName: "Active Campaigns",
            cellRenderer: ActiveCampaignsRenderer,
            minWidth: 200,
        },
        {
            field: "ns_status",
            headerName: "NS Status",
            cellRenderer: NsStatusRenderer,
            minWidth: 140,
        },
        {
            field: "nameservers",
            headerName: "Nameservers",
            flex: 2,
            cellRenderer: (params: CustomCellRendererProps<MDomain>) => {
                if (!params.data) return <span style={{ color: "text.secondary" }}>—</span>;
                
                return (
                    <span
                        onClick={() => handleOpenNameserverDialog(params.data!)}
                        style={{
                            cursor: "pointer",
                            color: "#1976d2",
                            fontWeight: 500,
                            textDecoration: "underline"
                        }}
                    >
                        Check NS Records
                    </span>
                );
            },
            minWidth: 150,
        },
        {
            field: "ns_mismatch_detected_at",
            headerName: "NS Issue Since",
            valueFormatter: timestampFormatter,
            minWidth: 180,
        },
        {
            field: "last_ns_check_at",
            headerName: "Last NS Check",
            valueFormatter: timestampFormatter,
            minWidth: 180,
        },
        {
            field: "burnt",
            headerName: "Burnt",
            cellRenderer: BurntStatusRenderer,
            minWidth: 120,
        },
        {
            field: "burnt_detected_on",
            headerName: "Burnt On",
            valueFormatter: timestampFormatter,
            minWidth: 180,
        },
        {
            field: "burnt_via",
            headerName: "Burnt Via",
            minWidth: 200,
        },
        {
            field: "burnt_at",
            headerName: "Burnt At",
            minWidth: 120,
        },
        {
            field: "registrar_name",
            headerName: "Domain Registrar Name",
            valueGetter: (params) => params.data?.registrar_name ? params.data.registrar_name : "Manual",
            minWidth: 140,
        },
        {
            field: "registrar_icon_name",
            headerName: "Registrar Icon",
            minWidth: 140,
        },
        {
            field: "is_automated_import",
            headerName: "Import Type",
            valueGetter: (params) => (params.data?.is_automated_import ? "Automated" : "Manual"),
            minWidth: 130,
        },
        {headerName: "Actions", colId: "actions", cellRenderer: ActionsButton, sortable: false, resizable: false, minWidth: 100,},
    ];

    // ============================================================
    // --------------------- MAIN RENDER CODE ---------------------
    // ============================================================

    if (fetchAllDomainsQuery.isLoading) {
        return <PageLoading />;
    } else if (fetchAllDomainsQuery.error as unknown as ApiRequestFailed) {
        return <PageDataErrorHandler error={fetchAllDomainsQuery.error as unknown as ApiRequestFailed} />;
    } else if (pageData) {
        return (
            <Stack 
                direction="column" 
                spacing={2} 
                sx={{ 
                    pb: 4, 
                    height: "100%", 
                    width: "100%", 
                    maxWidth: "100vw", 
                    overflowX: "hidden" 
                }}
            >
                <Typography fontWeight={"bold"} variant={"h5"}>
                    All Connected Domains
                </Typography>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "repeat(2, 1fr)",
                            sm: "repeat(4, 1fr)",
                            md: "repeat(7, 1fr)"
                        },
                        gap: 1.5,
                        width: "100%"
                    }}
                >
                    <Card>
                        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                            <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary" fontWeight="bold">Total Domains</Typography>
                                <Typography variant="h5" fontWeight="bold">{pageData.summary.total_domains}</Typography>
                            </Stack>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                            <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary" fontWeight="bold">Setup Complete</Typography>
                                <Typography variant="h5" fontWeight="bold" color="success.main">{pageData.summary.setup_complete_count}</Typography>
                            </Stack>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                            <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary" fontWeight="bold">Setup Incomplete</Typography>
                                <Typography variant="h5" fontWeight="bold" color="warning.main">{pageData.summary.setup_incomplete_count}</Typography>
                            </Stack>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                            <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary" fontWeight="bold">Random</Typography>
                                <Typography variant="h5" fontWeight="bold">{pageData.summary.naming_random_count}</Typography>
                            </Stack>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                            <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary" fontWeight="bold">Custom</Typography>
                                <Typography variant="h5" fontWeight="bold">{pageData.summary.naming_custom_count}</Typography>
                            </Stack>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                            <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary" fontWeight="bold">Male</Typography>
                                <Typography variant="h5" fontWeight="bold">{pageData.summary.naming_male_count}</Typography>
                            </Stack>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                            <Stack spacing={0.5}>
                                <Typography variant="caption" color="text.secondary" fontWeight="bold">Female</Typography>
                                <Typography variant="h5" fontWeight="bold">{pageData.summary.naming_female_count}</Typography>
                            </Stack>
                        </CardContent>
                    </Card>
                </Box>

                <Divider />
                <Box 
                    sx={{ 
                        width: "100%", 
                        minHeight: { xs: 400, md: 500 },
                        display: "flex", 
                        flexDirection: "column" 
                    }}
                >
                    <PaginatedServerSideDataTable
                        columns={columnDefs}
                        paginationData={pageData}
                        loading={fetchAllDomainsQuery.isLoading || fetchAllDomainsQuery.isFetching}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        onSearchChange={handleSearchChange}
                        searchPlaceholder="Search by domain, user email, or workspace..."
                        actions={(
                            <Box 
                                display="flex" 
                                alignItems="center" 
                                gap={1} 
                                flexWrap="wrap"
                                sx={{ width: { xs: "100%", sm: "auto" } }}
                            >
                                <IconButton onClick={openFilter} size="small" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                    <FilterListIcon fontSize="small" />
                                </IconButton>

                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<Email />}
                                    onClick={() => setSendEmailDialogOpen(true)}
                                    size="small"
                                    sx={{ flex: { xs: "1", sm: "none" } }}
                                >
                                    Test Mail
                                </Button>
                                
                                {ExportDomainCsvButton()}
                            </Box>
                        )}
                        columnLabels={columnCustomizerLabel}
                    />
                </Box>
                <SubdomainsDialog />
                <Popover
                    open={isFilterOpen}
                    anchorEl={filterAnchor}
                    onClose={closeFilter}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                    PaperProps={{ sx: { p: 2, width: 260 } }}
                >
                    <Stack spacing={2}>
                        {/* Setup Complete */}
                        <FormControl fullWidth size="small">
                            <InputLabel>Setup Complete</InputLabel>
                            <Select
                                value={filterSetupComplete}
                                label="Setup Complete"
                                onChange={(e) => setFilterSetupComplete(e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="yes">Yes</MenuItem>
                                <MenuItem value="no">No</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Warmup */}
                        <FormControl fullWidth size="small">
                            <InputLabel>Warmup</InputLabel>
                            <Select
                                value={filterWarmup}
                                label="Warmup"
                                onChange={(e) => setFilterWarmup(e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="in_progress">In Progress</MenuItem>
                                <MenuItem value="completed">Completed</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Blacklist */}
                        <FormControl fullWidth size="small">
                            <InputLabel>Blacklist</InputLabel>
                            <Select
                                value={filterBlacklist}
                                label="Blacklist"
                                onChange={(e) => setFilterBlacklist(e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="healthy">Healthy</MenuItem>
                                <MenuItem value="incomplete">Incomplete</MenuItem>
                                <MenuItem value="blacklisted">Blacklisted</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Buttons */}
                        <Stack direction="row" justifyContent="space-between" mt={1}>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    setFilterSetupComplete("");
                                    setFilterWarmup("");
                                    setFilterBlacklist("");
                                }}
                            >
                                Clear
                            </Button>

                            <Button
                                variant="contained"
                                onClick={() => {
                                    fetchAllDomainsQuery.refetch();
                                    closeFilter();
                                }}
                            >
                                Apply
                            </Button>
                        </Stack>
                    </Stack>
                </Popover>
                <SendEmailDialog 
                    open={sendEmailDialogOpen}
                    onClose={() => setSendEmailDialogOpen(false)}
                    allDomains={pageData?.data || []}
                />
                <NameserverDialog />
            </Stack>
        );
    } else {
        // Ideally it should not reach here.
        return <></>;
    }
}

function SendEmailDialog({ open, onClose, allDomains }: { open: boolean, onClose: () => void, allDomains: MDomain[] }) {
    const { enqueueSnackbar } = useSnackbar();
    
    // Form State
    const [selectedMsubId, setSelectedMsubId] = useState<string>("");
    const [selectedEmailId, setSelectedEmailId] = useState<string>("");
    const [senderName, setSenderName] = useState("");
    const [targetEmails, setTargetEmails] = useState("");
    const [subject, setSubject] = useState("Admin Test");
    const [body, setBody] = useState("");
    const [contentType, setContentType] = useState("text/html");
    const [delaySeconds, setDelaySeconds] = useState("0");
    const [openTracking, setOpenTracking] = useState(true);
    const [clickTracking, setClickTracking] = useState(true);
    const [oneClickUnsub, setOneClickUnsub] = useState(false);
    const [addUnsubLink, setAddUnsubLink] = useState(false);
    const [sendAsWarmup, setSendAsWarmup] = useState(false);

    const validDomains = allDomains?.filter(d => d.setup_complete) || [];
    const selectedDomainObj = validDomains.find(d => d.id.toString() === selectedMsubId.toString());
    const emailAccounts = selectedDomainObj?.subdomains?.flatMap(s => s.email_accounts) ?? [];
    const selectedEmailAccount = emailAccounts.find(a => a.id.toString() === selectedEmailId.toString());
    const selectedSenderAddress = selectedEmailAccount?.email_address ?? "";
    const senderAtIndex = selectedSenderAddress.lastIndexOf("@");
    const senderAddressPreview = sendAsWarmup && senderAtIndex > 0 && senderAtIndex < selectedSenderAddress.length - 1
        ? `${selectedSenderAddress.slice(0, senderAtIndex).replace(/\+warmup(?=\+|$)/gi, "")}+warmup${selectedSenderAddress.slice(senderAtIndex)}`
        : selectedSenderAddress;
    const isPlainText = contentType === "text/plain";

    const resetForm = () => {
        setSelectedMsubId("");
        setSelectedEmailId("");
        setSenderName("");
        setTargetEmails("");
        setDelaySeconds("0");
        setOpenTracking(true);
        setClickTracking(true);
        setOneClickUnsub(false);
        setAddUnsubLink(false);
        setSendAsWarmup(false);
    };

    const closeDialog = () => {
        setSendAsWarmup(false);
        onClose();
    };

    const handleDomainChange = (msubId: string) => {
        setSelectedMsubId(msubId);

        const accounts = validDomains
            .find(d => d.id.toString() === msubId.toString())
            ?.subdomains?.flatMap(s => s.email_accounts) ?? [];

        // Nothing to choose from when there is only one inbox, so pick it outright.
        const onlyAccount = accounts.length === 1 ? accounts[0] : null;
        setSelectedEmailId(onlyAccount ? onlyAccount.id.toString() : "");
        setSenderName(onlyAccount ? onlyAccount.username : "");
    };

    const handleEmailChange = (emailId: string) => {
        setSelectedEmailId(emailId);
        const account = emailAccounts.find(a => a.id.toString() === emailId.toString());
        setSenderName(account?.username ?? "");
    };

    const sendEmailMutation = useMutation({
        mutationKey: ["sendEmailMutation"],
        mutationFn: (payload: any) => authenticateAndPostData("/admin/send-test-email/", payload),
        gcTime: 0,
		retry: retryFn,
        onSuccess: (response) => {
            enqueueSnackbar(response.data.message, { variant: "success" });
            closeDialog();
            resetForm();
        },
        onError: (error: ApiRequestFailed) => {
			console.error(error);
			enqueueSnackbar(error.data.message || "Failed to send", { variant: "error" });
		}
    });

    const handleSend = () => {
        if (!selectedMsubId || !selectedEmailId || !targetEmails) {
            enqueueSnackbar("Please fill in all required fields", { variant: "warning" });
            return;
        }

        const delay = parseInt(delaySeconds || "0", 10);

        sendEmailMutation.mutate({
            msub_id: selectedMsubId,
            domain_name: selectedDomainObj?.domain,
            email_id: selectedEmailId,
            from_name: senderName.trim(),
            subject,
            body,
            test_email_targets: targetEmails.split(",").map(e => e.trim()),
            content_type: contentType,
            delay_seconds: delay,
            open_tracking_enabled: openTracking,
            click_tracking_enabled: clickTracking,
            one_click_unsubscribe_header: oneClickUnsub,
            add_unsub_link: addUnsubLink,
            send_as_warmup: sendAsWarmup,
        });
    };

    return (
        <Dialog open={open} onClose={closeDialog} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Send Test Email</Typography>
                <IconButton onClick={closeDialog}><Close /></IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent>
                <Stack spacing={2.5} sx={{ mt: 1 }}>
                    {/* Domain Selection Dropdown */}
                    <FormControl fullWidth required>
                        <InputLabel>Select Sending Domain</InputLabel>
                        <Select
                            value={selectedMsubId}
                            label="Select Sending Domain"
                            onChange={(e) => handleDomainChange(e.target.value)}
                        >
                            {validDomains.map((d) => (
                                <MenuItem key={d.id} value={d.id}>
                                    {d.domain} ({d.workspace_name})
                                </MenuItem>
                            ))}
                        </Select>
                        {validDomains.length === 0 && (
                            <Typography variant="caption" color="error">No domains with completed setup found.</Typography>
                        )}
                    </FormControl>

                    {/* Sending Email Selection Dropdown */}
                    <FormControl fullWidth required disabled={!selectedMsubId || emailAccounts.length === 0}>
                        <InputLabel>Select Sending Email</InputLabel>
                        <Select
                            value={selectedEmailId}
                            label="Select Sending Email"
                            onChange={(e) => handleEmailChange(e.target.value)}
                        >
                            {emailAccounts.map((a) => (
                                <MenuItem key={a.id} value={a.id}>
                                    {a.email_address} — {a.username}
                                </MenuItem>
                            ))}
                        </Select>
                        {selectedMsubId && emailAccounts.length === 0 && (
                            <Typography variant="caption" color="error">No inboxes found for this domain.</Typography>
                        )}
                    </FormControl>

                    <TextField
                        label="Sender Name"
                        fullWidth
                        value={senderName}
                        onChange={(e) => setSenderName(e.target.value)}
                        helperText="Shown to the recipient. Defaults to the selected inbox's name."
                    />

                    <TextField
                        label="Recipient Emails"
                        placeholder="email1@test.com, email2@test.com"
                        fullWidth
                        required
                        value={targetEmails}
                        onChange={(e) => setTargetEmails(e.target.value)}
                    />

                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="Subject"
                            fullWidth
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                        />

                        <TextField
                            label="Delay (sec)"
                            type="number"
                            value={delaySeconds}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === "" || /^\d+$/.test(val)) {
                                    setDelaySeconds(val);
                                }
                            }}
                            sx={{ width: 160, flexShrink: 0 }}
                            helperText="Between recipients"
                        />
                    </Stack>

                    <Box>
                        <Typography variant="caption" fontWeight="bold">Content Type</Typography>
                        <ToggleButtonGroup
                            value={contentType}
                            exclusive
                            onChange={(_, val) => val && setContentType(val)}
                            fullWidth
                            size="small"
                            sx={{ mt: 0.5 }}
                        >
                            <ToggleButton value="text/html">HTML</ToggleButton>
                            <ToggleButton value="text/plain">Plain Text</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    <Box>
                        <Typography variant="caption" fontWeight="bold">Sending Options</Typography>
                        <Stack sx={{ mt: 0.5 }}>
                            <FormControlLabel
                                control={<Switch checked={openTracking} onChange={(e) => setOpenTracking(e.target.checked)} />}
                                label="Open tracking"
                            />
                            <FormControlLabel
                                control={<Switch checked={clickTracking} onChange={(e) => setClickTracking(e.target.checked)} />}
                                label="Click tracking"
                            />
                            <FormControlLabel
                                control={<Switch checked={sendAsWarmup} onChange={(e) => setSendAsWarmup(e.target.checked)} />}
                                label="Send from +warmup address"
                            />
                            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                                Sender address: {senderAddressPreview || "Select a sending email"}
                            </Typography>
                            {sendAsWarmup && (
                                <Typography variant="caption" color="text.secondary">
                                    This send follows warmup bounce safeguards.
                                </Typography>
                            )}
                            <Stack direction="row" spacing={2}>
                                <FormControlLabel
                                    sx={{ flex: 1, mr: 0 }}
                                    control={<Switch checked={oneClickUnsub} onChange={(e) => setOneClickUnsub(e.target.checked)} />}
                                    label="One-click unsubscribe header"
                                />
                                <FormControlLabel
                                    sx={{ flex: 1, mr: 0 }}
                                    control={<Switch checked={addUnsubLink} onChange={(e) => setAddUnsubLink(e.target.checked)} />}
                                    label="Add Unsubscribe Text"
                                />
                            </Stack>
                        </Stack>
                        {isPlainText && (openTracking || clickTracking) && (
                            <Typography variant="caption" color="text.secondary">
                                Tracking does not apply to plain text emails.
                            </Typography>
                        )}
                    </Box>

                    {isPlainText ? (
                        <TextField
                            label="Message Body"
                            multiline
                            rows={5}
                            fullWidth
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                        />
                    ) : (
                        <Box>
                            <Typography variant="caption" fontWeight="bold">Message Body</Typography>
                            <Box
                                sx={{
                                    mt: 0.5,
                                    "& .ql-toolbar": {
                                        borderTopLeftRadius: 4,
                                        borderTopRightRadius: 4,
                                        borderColor: "divider",
                                        color: "text.primary",
                                    },
                                    "& .ql-container": {
                                        borderBottomLeftRadius: 4,
                                        borderBottomRightRadius: 4,
                                        borderColor: "divider",
                                        color: "text.primary",
                                    },
                                    "& .ql-editor": {
                                        minHeight: 160,
                                        color: "text.primary",
                                    },
                                    "& .ql-editor.ql-blank::before": {
                                        color: "text.secondary",
                                    },
                                    "& .ql-snow button, & .ql-snow .ql-picker-label, & .ql-snow .ql-picker-item": {
                                        color: "text.primary",
                                    },
                                    "& .ql-snow .ql-stroke": {
                                        stroke: "currentColor",
                                    },
                                    "& .ql-snow .ql-fill, & .ql-snow .ql-stroke.ql-fill": {
                                        fill: "currentColor",
                                    },
                                    "& .ql-snow .ql-picker-options": {
                                        backgroundColor: "background.paper",
                                        borderColor: "divider",
                                    },
                                    "& .ql-snow button:hover, & .ql-snow button:focus, & .ql-snow button.ql-active, & .ql-snow .ql-picker-label:hover, & .ql-snow .ql-picker-label.ql-active, & .ql-snow .ql-picker-item:hover, & .ql-snow .ql-picker-item.ql-selected": {
                                        color: "primary.main",
                                    },
                                }}
                            >
                                <ReactQuill
                                    theme="snow"
                                    value={body}
                                    onChange={setBody}
                                    modules={TEST_EMAIL_EDITOR_MODULES}
                                    placeholder="Message Body"
                                />
                            </Box>
                        </Box>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: 2.5 }}>
                <Button onClick={closeDialog} color="inherit">Cancel</Button>
                <Button 
                    variant="contained" 
                    onClick={handleSend}
                    disabled={sendEmailMutation.isPending || !selectedMsubId || !selectedEmailId}
                >
                    {sendEmailMutation.isPending ? <CircularProgress size={24} /> : "Send Email"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
